import type { IconProps } from '@carboncopyco/icons';
import classNames from 'classnames';
import { type ReactElement, cloneElement } from 'react';
import { ColorHex } from '../../constants';
import { Spacer } from '../Spacer';
import { TooltipBubble } from '../TooltipBubble';
import { TextButton } from '../Typography';

export interface CapsuleProps {
  selected?: boolean;
  label?: string;
  onClick?: () => void;
  icon?: ReactElement<IconProps>;
  leftIcon?: ReactElement<IconProps>;
  rightIcon?: ReactElement<IconProps>;
  small?: boolean;
  disabled?: boolean;
  ellipsis?: boolean;
  interactive?: boolean;
  tooltipLabel?: string;
}

export const Capsule = ({
  interactive = true,
  selected = false,
  small = false,
  label,
  tooltipLabel,
  onClick,
  icon,
  leftIcon,
  rightIcon,
  disabled,
  ellipsis,
}: CapsuleProps) => {
  const iconColor =
    selected && !disabled
      ? ColorHex.accent3100
      : disabled
        ? ColorHex.natural200
        : ColorHex.primary;

  return (
    <TooltipBubble
      placement="top"
      title={tooltipLabel ?? label ?? ''}
      disabled={!tooltipLabel || !ellipsis || !label}
    >
      <div
        onClick={onClick}
        className={classNames(
          'flex rounded-ds-40 border border-solid box-border',
          {
            'bg-ds-natural-200-a border-transparent': disabled,
            'bg-ds-primary border-ds-primary': selected && !disabled,
            'hover:border-ds-primary-300':
              !selected && !disabled && interactive,
            'bg-ds-natural-100 border-ds-natural-100': !selected && !disabled,
            'py-1 px-2': small,
            'py-ds-p-7 px-2': !small,
            'cursor-default': !onClick,
            'cursor-pointer': !!onClick,
            'max-w-ds-50': ellipsis,
          }
        )}
      >
        {icon ? (
          <div className="flex items-center justify-center">
            {cloneElement(icon, {
              size: 20,
              color: iconColor,
            })}
          </div>
        ) : (
          <>
            {leftIcon && (
              <div className="flex items-center justify-center">
                {cloneElement(leftIcon, {
                  size: 20,
                  color: iconColor,
                })}
                <Spacer amount={0.25} />
              </div>
            )}
            {label && (
              <TextButton
                label={label}
                ellipsis={ellipsis}
                color={
                  selected
                    ? 'text-ds-accent3-100'
                    : disabled
                      ? 'text-ds-natural-200'
                      : 'text-ds-primary'
                }
              />
            )}

            {rightIcon && (
              <div className="flex items-center justify-center">
                {cloneElement(rightIcon, {
                  size: 20,
                  color: iconColor,
                })}
                <Spacer amount={0.25} />
              </div>
            )}
          </>
        )}
      </div>
    </TooltipBubble>
  );
};
